/**
 * D3 Pie/Donut implementation. Uses d3-shape pie + arc generators directly
 * (no Visx wrapper). Active-arc hover grows the outer radius by 6px to
 * mirror the Recharts renderActiveShape behavior.
 */

import { useMemo, useState, useRef, useEffect } from 'react';
import { pie as d3Pie, arc as d3Arc, type PieArcDatum } from 'd3-shape';
import type { PieChartProps, CommonChartProps, CategoricalDatum } from '../types';
import { chartTheme } from '../theme';
import { DrillAwareTooltip } from '@/components/analytics/DrillAwareTooltip';

type Props = PieChartProps & CommonChartProps;

function useMeasuredRef<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, size] as const;
}

export function D3Pie({ type, data, minLabelPercent = 0.08, enableHover = true }: Props): JSX.Element {
  const [ref, { width, height }] = useMeasuredRef<HTMLDivElement>();
  const theme = chartTheme('d3').theme;
  const palette = theme.categoricalPalette;
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [hover, setHover] = useState<{ x: number; y: number; datum: CategoricalDatum; index: number } | null>(null);

  const radius = Math.min(width, height) / 2;
  const outerRadius = radius * 0.92;
  const innerRadius = type === 'donut' ? radius * 0.55 : 0;

  const arcs = useMemo(() => {
    if (data.length === 0 || radius <= 0) return [];
    const pieGen = d3Pie<CategoricalDatum>().value((d) => d.value).padAngle(0.005);
    return pieGen(data);
  }, [data, radius]);

  const arcGen = useMemo(() => d3Arc<PieArcDatum<CategoricalDatum>>().innerRadius(innerRadius), [innerRadius]);

  const total = data.reduce((acc, d) => acc + d.value, 0);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {width > 0 && height > 0 && (
        <svg width={width} height={height}>
          <g transform={`translate(${width / 2},${height / 2})`}>
            {arcs.map((arc, i) => {
              const isActive = enableHover && activeIndex === i;
              const adjustedOuter = isActive ? outerRadius + 6 : outerRadius;
              const path = arcGen.outerRadius(adjustedOuter)(arc) ?? '';
              const centroid = d3Arc<PieArcDatum<CategoricalDatum>>()
                .innerRadius(innerRadius)
                .outerRadius(outerRadius)
                .centroid(arc);
              const fill = (arc.data.color as string) ?? palette[i % palette.length];
              const percent = total > 0 ? arc.value / total : 0;
              const showLabel = percent >= minLabelPercent;
              return (
                <g
                  key={`arc-${i}`}
                  onMouseEnter={(e) => {
                    if (!enableHover) return;
                    setActiveIndex(i);
                    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.getBoundingClientRect();
                    setHover({
                      x: e.clientX - (rect?.left ?? 0),
                      y: e.clientY - (rect?.top ?? 0),
                      datum: arc.data,
                      index: i,
                    });
                  }}
                  onMouseMove={(e) => {
                    if (!hover) return;
                    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement | null)?.getBoundingClientRect();
                    setHover({
                      x: e.clientX - (rect?.left ?? 0),
                      y: e.clientY - (rect?.top ?? 0),
                      datum: arc.data,
                      index: i,
                    });
                  }}
                  onMouseLeave={() => {
                    if (!enableHover) return;
                    setActiveIndex(null);
                    setHover(null);
                  }}
                  style={{ cursor: enableHover ? 'pointer' : 'default' }}
                >
                  <path
                    d={path}
                    fill={fill}
                    style={{ transition: 'd 120ms ease-out' }}
                  />
                  {showLabel && (
                    <text x={centroid[0]} y={centroid[1]} textAnchor="middle" dominantBaseline="central" fill="#ffffff" fontSize={11} fontWeight={600}>
                      {`${(percent * 100).toFixed(0)}%`}
                    </text>
                  )}
                </g>
              );
            })}
          </g>
        </svg>
      )}
      {hover && (
        <div
          style={{
            position: 'absolute',
            left: Math.min(hover.x + 12, width - 200),
            top: Math.max(hover.y - 40, 0),
            pointerEvents: 'none',
          }}
        >
          <DrillAwareTooltip
            rows={[
              {
                color: (hover.datum.color as string) ?? palette[hover.index % palette.length],
                label: hover.datum.label,
                value: hover.datum.value,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}
